import React, { useEffect, useState } from "react";
import { Route, Routes, NavLink } from "react-router-dom";
import {
  House, Users, Storefront, Package, Receipt, Warning, Gear, CheckCircle, Wallet,
  CreditCard, ClipboardText, EnvelopeSimple,
} from "@phosphor-icons/react";
import DashboardShell from "@/components/DashboardShell";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import Avatar from "@/components/Avatar";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const links = [
  { to: "/dashboard/admin", end: true, label: "Overview", Icon: House },
  { to: "/dashboard/admin/users", label: "Users", Icon: Users },
  { to: "/dashboard/admin/sellers", label: "Sellers", Icon: Storefront },
  { to: "/dashboard/admin/services", label: "Services", Icon: Package },
  { to: "/dashboard/admin/orders", label: "Orders", Icon: Receipt },
  { to: "/dashboard/admin/payments", label: "Payments", Icon: CreditCard },
  { to: "/dashboard/admin/disputes", label: "Disputes", Icon: Warning },
  { to: "/dashboard/admin/payouts", label: "Payouts", Icon: Wallet },
  { to: "/dashboard/admin/audit", label: "Audit logs", Icon: ClipboardText },
  { to: "/dashboard/admin/email-log", label: "Email log", Icon: EnvelopeSimple },
  { to: "/dashboard/admin/settings", label: "Settings", Icon: Gear },
];

function Card({ title, children, action }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="font-display font-bold text-mv-secondary">{title}</div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider font-bold"><Icon weight="duotone" /> {label}</div>
      <div className="font-display font-black text-3xl text-mv-secondary mt-2">{value}</div>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setS(data));
    api.get("/admin/audit-logs?limit=5").then(({ data }) => setRecent(data));
  }, []);
  if (!s) return null;
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Users" value={s.users} icon={Users} />
        <Stat label="Sellers" value={s.sellers} icon={Storefront} />
        <Stat label="Services" value={s.services} icon={Package} />
        <Stat label="Orders" value={s.orders} icon={Receipt} />
        <Stat label="GMV" value={`$${s.gmv?.toFixed(0)}`} icon={Wallet} />
        <Stat label="Platform revenue" value={`$${s.platform_revenue?.toFixed(0)}`} icon={CheckCircle} />
        <Stat label="Open disputes" value={s.open_disputes} icon={Warning} />
      </div>
      <RecentActivity items={recent} />
    </>
  );
}

const ACTION_META = {
  update_settings:    { label: "Updated platform settings", Icon: Gear,       tone: "bg-mv-primary/10 text-mv-primary" },
  refund_payment:     { label: "Refunded payment",          Icon: CreditCard, tone: "bg-rose-100 text-rose-700" },
  resolve_dispute:    { label: "Resolved dispute",          Icon: Warning,    tone: "bg-amber-100 text-amber-800" },
  tier_change:        { label: "Changed seller tier",       Icon: Storefront, tone: "bg-mv-primary/10 text-mv-primary" },
  approve_seller:     { label: "Updated seller status",     Icon: Storefront, tone: "bg-mv-primary/10 text-mv-primary" },
  approve_service:    { label: "Approved service",          Icon: Package,    tone: "bg-mv-primary/10 text-mv-primary" },
  reject_service:     { label: "Rejected service",          Icon: Package,    tone: "bg-rose-100 text-rose-700" },
  feature_service:    { label: "Featured service",          Icon: Package,    tone: "bg-mv-accent/20 text-mv-secondary" },
  update_service:     { label: "Updated service",           Icon: Package,    tone: "bg-gray-100 text-gray-700" },
  payout_marked_paid: { label: "Marked payout paid",        Icon: Wallet,     tone: "bg-mv-primary/10 text-mv-primary" },
  update_user:        { label: "Updated user",              Icon: Users,      tone: "bg-gray-100 text-gray-700" },
};

function _relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function _targetSummary(item) {
  const { target_type, target_id, after, before } = item;
  if (target_type === "platform_settings" && after && typeof after === "object") {
    const keys = Object.keys(after);
    if (keys.length) {
      const k = keys[0];
      return `${k}: ${before?.[k] ?? "?"} → ${after?.[k]}`;
    }
    return "default";
  }
  if (target_type === "seller_profile" && after?.tier) {
    return `tier → ${after.tier}`;
  }
  if (target_type === "seller_profile" && typeof after?.approved === "boolean") {
    return after.approved ? "approved" : "suspended";
  }
  if (target_type === "service" && after?.status) {
    return `status → ${after.status}`;
  }
  if (target_type === "service" && typeof after?.featured === "boolean") {
    return after.featured ? "featured" : "unfeatured";
  }
  if (target_type === "payment" && after?.amount != null) {
    return `$${after.amount}`;
  }
  return target_id ? target_id.slice(0, 8) : "";
}

function RecentActivity({ items }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="recent-activity">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="font-display font-bold text-mv-secondary">Recent admin activity</div>
        <NavLink to="/dashboard/admin/audit" className="text-xs font-bold text-mv-primary hover:underline" data-testid="recent-activity-view-all">
          View all →
        </NavLink>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-6 text-sm text-gray-500">No admin actions yet.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {items.map((it) => {
            const meta = ACTION_META[it.action] || { label: it.action.replace(/_/g, " "), Icon: ClipboardText, tone: "bg-gray-100 text-gray-700" };
            const Icon = meta.Icon;
            return (
              <li key={it.id} className="px-5 py-3 flex items-start gap-3" data-testid={`activity-row-${it.id}`}>
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.tone}`}>
                  <Icon size={16} weight="duotone" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm text-mv-secondary truncate">{meta.label}</span>
                    <span className="text-xs text-gray-500 capitalize">· {it.target_type?.replace(/_/g, " ")}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate mt-0.5">
                    {_targetSummary(it)}
                  </div>
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    {it.actor_email} · {_relTime(it.created_at)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);
  return (
    <Card title="All users">
      <div className="mv-table-wrap"><table className="w-full text-sm" data-testid="admin-users-table">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-gray-500"><th>Name</th><th>Email</th><th>Role</th><th>Joined</th></tr></thead>
        <tbody>{users.map((u) => (
          <tr key={u.id} className="border-t border-gray-100">
            <td className="py-2 font-bold">{u.name}</td><td>{u.email}</td><td><span className="capitalize px-2 py-0.5 rounded-full bg-mv-primary/10 text-mv-primary text-xs font-bold">{u.role}</span></td><td className="text-gray-500">{new Date(u.created_at).toLocaleDateString()}</td>
          </tr>
        ))}</tbody>
      </table></div>
    </Card>
  );
}

function SellersAdmin() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/sellers").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const setTier = async (uid, tier) => {
    await api.patch(`/admin/sellers/${uid}`, { tier });
    toast.success("Updated"); load();
  };
  const toggleApproved = async (uid, approved) => {
    await api.patch(`/admin/sellers/${uid}`, { approved: !approved });
    toast.success("Updated"); load();
  };
  return (
    <Card title="Sellers">
      <ul className="divide-y divide-gray-100" data-testid="admin-sellers-list">
        {list.map((s) => (
          <li key={s.user_id} className="py-3 flex items-start sm:items-center gap-3 flex-wrap sm:flex-nowrap">
            <Avatar src={s.user?.avatar_url} name={s.user?.name || ""} size={40} className="shrink-0" testId={`admin-seller-avatar-${s.user?.id || ""}`} />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-mv-secondary truncate">{s.user?.name}</div>
              <div className="text-xs text-gray-500 truncate">{s.user?.email} · {s.stats?.completed_orders} orders</div>
            </div>
            <div className="w-full sm:w-auto flex gap-2 sm:gap-2">
              <Select value={s.tier} onValueChange={(v) => setTier(s.user_id, v)}>
                <SelectTrigger className="w-full sm:w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New seller</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="certified">Certified Expert</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => toggleApproved(s.user_id, s.approved)} className="shrink-0">
                {s.approved ? "Suspend" : "Approve"}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ServicesAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/services").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const set = async (id, payload) => { await api.patch(`/admin/services/${id}`, payload); toast.success("Updated"); load(); };
  return (
    <Card title="Services">
      <div className="mv-table-wrap"><table className="w-full text-sm" data-testid="admin-services-table">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-gray-500"><th>Title</th><th>Status</th><th>Featured</th><th>Actions</th></tr></thead>
        <tbody>{items.map((s) => (
          <tr key={s.id} className="border-t border-gray-100">
            <td className="py-2 font-bold">{s.title}</td>
            <td className="capitalize">{s.status}</td>
            <td>{s.featured ? "Yes" : "No"}</td>
            <td className="space-x-2">
              <Button size="sm" variant="outline" onClick={() => set(s.id, { status: s.status === "active" ? "paused" : "active" })}>{s.status === "active" ? "Pause" : "Activate"}</Button>
              <Button size="sm" variant="outline" onClick={() => set(s.id, { featured: !s.featured })}>{s.featured ? "Unfeature" : "Feature"}</Button>
            </td>
          </tr>
        ))}</tbody>
      </table></div>
    </Card>
  );
}

function OrdersAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/orders").then(({ data }) => setItems(data)); }, []);
  return (
    <Card title="All orders">
      <div className="mv-table-wrap"><table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-gray-500"><th>Service</th><th>Total</th><th>Status</th><th>Date</th></tr></thead>
        <tbody>{items.map((o) => (
          <tr key={o.id} className="border-t border-gray-100">
            <td className="py-2 font-bold">{o.service_title}</td>
            <td>${o.total}</td>
            <td className="capitalize">{o.status.replace(/_/g, " ")}</td>
            <td className="text-gray-500">{new Date(o.created_at).toLocaleDateString()}</td>
          </tr>
        ))}</tbody>
      </table></div>
    </Card>
  );
}

function DisputesAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/disputes").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const resolve = async (id, decision) => {
    try { await api.post(`/disputes/${id}/resolve`, { decision }); toast.success("Resolved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card title="Disputes">
      {items.length === 0 ? <div className="text-sm text-gray-500">No disputes.</div> :
        <ul className="divide-y divide-gray-100" data-testid="admin-disputes-list">
          {items.map((d) => (
            <li key={d.id} className="py-3">
              <div className="font-bold text-mv-secondary">{d.reason}</div>
              <div className="text-sm text-gray-700 mt-1">{d.description}</div>
              {d.files?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  {d.files.map((fid) => (
                    <a key={fid} target="_blank" rel="noreferrer"
                       href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}?auth=${localStorage.getItem("mv_token")}`}
                       className="px-2 py-1 rounded-full bg-mv-primary/10 text-mv-primary font-bold hover:bg-mv-primary hover:text-white">
                      📎 Buyer evidence
                    </a>
                  ))}
                </div>
              )}
              {d.seller_response && <div className="text-sm mt-2 bg-gray-50 rounded-lg p-2"><b>Seller:</b> {d.seller_response}</div>}
              {d.seller_response_files?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  {d.seller_response_files.map((fid) => (
                    <a key={fid} target="_blank" rel="noreferrer"
                       href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}?auth=${localStorage.getItem("mv_token")}`}
                       className="px-2 py-1 rounded-full bg-mv-secondary/10 text-mv-secondary font-bold hover:bg-mv-secondary hover:text-white">
                      📎 Seller response file
                    </a>
                  ))}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-1">Status: {d.status} · Order {d.order_id.slice(0,8)}</div>
              {!d.status.startsWith("resolved") && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => resolve(d.id, "resolved_refund")} className="bg-rose-600 hover:bg-rose-700 text-white">Full refund</Button>
                  <Button size="sm" onClick={() => resolve(d.id, "resolved_release")} className="bg-mv-primary text-white">Release to seller</Button>
                  <Button size="sm" variant="outline" onClick={() => resolve(d.id, "resolved_cancel")}>Cancel order</Button>
                </div>
              )}
            </li>
          ))}
        </ul>}
    </Card>
  );
}

function PayoutsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/payouts").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const mark = async (id) => { await api.post(`/payments/payouts/${id}/mark-paid`); load(); };
  return (
    <Card title="Payouts">
      <div className="mv-table-wrap"><table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-gray-500"><th>Date</th><th>Seller</th><th>Amount</th><th>Status</th><th></th></tr></thead>
        <tbody>{items.map((p) => (
          <tr key={p.id} className="border-t border-gray-100">
            <td className="py-2">{new Date(p.created_at).toLocaleDateString()}</td>
            <td className="text-xs text-gray-500">{p.seller_id.slice(0,8)}</td>
            <td>${p.amount}</td>
            <td className="capitalize">{p.status}</td>
            <td>{p.status !== "paid" && <Button size="sm" onClick={() => mark(p.id)} className="bg-mv-primary text-white">Mark paid</Button>}</td>
          </tr>
        ))}</tbody>
      </table></div>
    </Card>
  );
}

function PaymentsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/payments").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const refund = async (id, partial = false) => {
    const amt = partial ? Number(prompt("Refund amount (USD):") || 0) : null;
    if (partial && (!amt || amt <= 0)) return;
    try {
      const body = amt ? { amount: amt } : {};
      await api.post(`/payments/refund/${id}`, body);
      toast.success("Refund processed (mocked Stripe call)");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card title="All payments">
      <div className="mv-table-wrap"><table className="w-full text-sm" data-testid="admin-payments-table">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-gray-500">
          <th>Date</th><th>Order</th><th>Amount</th><th>Fee</th><th>Status</th><th>Method</th><th></th>
        </tr></thead>
        <tbody>{items.map((p) => (
          <tr key={p.id} className="border-t border-gray-100">
            <td className="py-2 text-gray-500">{new Date(p.created_at).toLocaleDateString()}</td>
            <td className="text-xs text-gray-500">{p.order_id.slice(0,8)}</td>
            <td>${p.amount}</td>
            <td className="text-mv-primary font-bold">${p.platform_fee}</td>
            <td><span className="capitalize px-2 py-0.5 rounded-full bg-mv-primary/10 text-mv-primary text-xs font-bold">{p.status.replace(/_/g, " ")}</span></td>
            <td className="text-xs text-gray-500">{p.method}</td>
            <td className="space-x-1">
              {p.status === "held" && <Button size="sm" variant="outline" onClick={() => refund(p.id)} data-testid={`refund-${p.id}`}>Full refund</Button>}
              {p.status === "held" && <Button size="sm" variant="outline" onClick={() => refund(p.id, true)}>Partial</Button>}
            </td>
          </tr>
        ))}</tbody>
      </table></div>
    </Card>
  );
}

function AuditLogs() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs?limit=200").then(({ data }) => setItems(data)); }, []);
  return (
    <Card title="Audit log">
      {items.length === 0 ? <div className="text-sm text-gray-500">No admin actions yet.</div> :
        <ul className="divide-y divide-gray-100" data-testid="audit-logs-list">
          {items.map((l) => (
            <li key={l.id} className="py-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-bold text-mv-secondary capitalize">{l.action.replace(/_/g, " ")}</span>
                <span className="text-xs text-gray-500">{new Date(l.created_at).toLocaleString()}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                <b>{l.actor_email}</b> · {l.target_type}/{l.target_id?.slice(0, 8)}
              </div>
              {(l.before || l.after) && (
                <pre className="mt-2 text-xs bg-gray-50 rounded-lg p-2 overflow-x-auto">
{`before: ${JSON.stringify(l.before)}
after:  ${JSON.stringify(l.after)}${l.note ? `
note:   ${l.note}` : ""}`}
                </pre>
              )}
            </li>
          ))}
        </ul>}
    </Card>
  );
}

function EmailLog() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/email-log?limit=200").then(({ data }) => setItems(data)); }, []);
  return (
    <Card title="Email log (MOCKED — no real send)">
      {items.length === 0 ? <div className="text-sm text-gray-500">No emails yet.</div> :
        <ul className="divide-y divide-gray-100" data-testid="email-log-list">
          {items.map((e) => (
            <li key={e.id} className="py-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-bold text-mv-secondary">{e.subject}</span>
                <span className="text-xs text-gray-500">{new Date(e.created_at).toLocaleString()}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">to: <b>{e.to}</b> · from: {e.from_email} · provider: {e.provider}</div>
            </li>
          ))}
        </ul>}
    </Card>
  );
}

function Settings() {
  const [s, setS] = useState(null);
  const load = () => api.get("/admin/settings").then(({ data }) => setS(data));
  useEffect(() => { load(); }, []);
  const save = async () => { await api.patch("/admin/settings", s); toast.success("Saved"); load(); };
  if (!s) return null;
  return (
    <Card title="Platform settings">
      <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
        <div>
          <Label>Platform fee %</Label>
          <Input type="number" value={s.platform_fee_percent} onChange={(e) => setS({ ...s, platform_fee_percent: Number(e.target.value) })} data-testid="settings-fee" />
        </div>
        <div>
          <Label>Auto-release days</Label>
          <Input type="number" value={s.auto_release_days} onChange={(e) => setS({ ...s, auto_release_days: Number(e.target.value) })} data-testid="settings-release" />
        </div>
        <div>
          <Label>Email "from" address</Label>
          <Input value={s.from_email || "no-reply@marketvyne.com"} onChange={(e) => setS({ ...s, from_email: e.target.value })} data-testid="settings-from-email" />
        </div>
        <div>
          <Label>Email "from" name</Label>
          <Input value={s.from_name || "MarketVyne"} onChange={(e) => setS({ ...s, from_name: e.target.value })} />
        </div>
      </div>
      <Button onClick={save} className="mt-4 bg-mv-primary text-white font-bold" data-testid="settings-save">Save settings</Button>
    </Card>
  );
}

export default function AdminDashboard() {
  return (
    <Routes>
      <Route element={<DashboardShell links={links} title="Admin console" />}>
        <Route index element={<Overview />} />
        <Route path="users" element={<UsersAdmin />} />
        <Route path="sellers" element={<SellersAdmin />} />
        <Route path="services" element={<ServicesAdmin />} />
        <Route path="orders" element={<OrdersAdmin />} />
        <Route path="payments" element={<PaymentsAdmin />} />
        <Route path="disputes" element={<DisputesAdmin />} />
        <Route path="payouts" element={<PayoutsAdmin />} />
        <Route path="audit" element={<AuditLogs />} />
        <Route path="email-log" element={<EmailLog />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}
